import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import soundtrackAsset from "@/assets/rooted-in-the-sun.mp3.asset.json";

export const SOUNDTRACK_TITLE = "Rooted in the Sun";

interface SoundtrackContextValue {
  isPlaying: boolean;
  hasStarted: boolean;
  currentTime: number;
  duration: number;
  toggle: () => void;
  play: () => void;
  pause: () => void;
}

const SoundtrackContext = createContext<SoundtrackContextValue | null>(null);

export const SoundtrackProvider = ({ children }: { children: ReactNode }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Create the audio element once — it persists for the lifetime of the app,
  // so playback continues across route changes without restarting.
  if (audioRef.current === null && typeof Audio !== "undefined") {
    const audio = new Audio(soundtrackAsset.url);
    audio.preload = "none";
    audioRef.current = audio;
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => {
      setIsPlaying(true);
      setHasStarted(true);
    };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
    };
  }, []);

  const value = useMemo<SoundtrackContextValue>(() => {
    const play = () => {
      void audioRef.current?.play().catch(() => {
        /* ignore — user can retry */
      });
    };
    const pause = () => audioRef.current?.pause();
    const toggle = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) play();
      else pause();
    };
    return { isPlaying, hasStarted, currentTime, duration, toggle, play, pause };
  }, [isPlaying, hasStarted, currentTime, duration]);

  return (
    <SoundtrackContext.Provider value={value}>
      {children}
    </SoundtrackContext.Provider>
  );
};

export const useSoundtrack = () => {
  const ctx = useContext(SoundtrackContext);
  if (!ctx) {
    throw new Error("useSoundtrack must be used within SoundtrackProvider");
  }
  return ctx;
};
